import type { ResumeData } from "@/types/resume";

export const sampleResumes: { name: string; role: string; template: string; data: ResumeData }[] = [
  {
    name: "Sarah Chen",
    role: "Marketing Graduate",
    template: "ats-classic",
    data: {
      personalInfo: {
        fullName: "Sarah Chen",
        professionalTitle: "Marketing & Communications Graduate",
        email: "sarah.chen@email.com",
        phone: "+1 (415) 555-0142",
        city: "San Francisco",
        country: "United States",
        linkedin: "linkedin.com/in/sarahchen",
        portfolio: "sarahchen.design",
      },
      summary: "Recent UC Berkeley graduate with a passion for digital marketing and brand storytelling. Completed three internships in social media management and content creation, growing engagement by 45% across platforms. Seeking an entry-level marketing role where I can leverage data-driven insights and creative strategy.",
      workExperience: [
        {
          id: "s1", jobTitle: "Marketing Intern", company: "TechStartup Inc.", location: "San Francisco, CA",
          startDate: "2025-06", endDate: "", currentlyWorking: true,
          responsibilities: [
            "Managed social media content calendar across Instagram, LinkedIn, and TikTok, increasing follower count by 2,400 in 3 months",
            "Created weekly analytics reports tracking engagement rates, reach, and conversion metrics",
            "Collaborated with design team to produce 50+ branded graphics for product launches",
          ],
          achievements: [],
        },
        {
          id: "s2", jobTitle: "Content Writing Intern", company: "Digital Agency Co.", location: "Remote",
          startDate: "2025-01", endDate: "2025-05", currentlyWorking: false,
          responsibilities: [
            "Wrote 30+ SEO-optimized blog posts averaging 1,200 words each, driving 18% increase in organic traffic",
            "Conducted keyword research using Ahrefs and SEMrush to inform content strategy",
          ],
          achievements: [],
        },
      ],
      education: [
        {
          id: "e1", degree: "B.A. Marketing & Communications", institution: "University of California, Berkeley",
          location: "Berkeley, CA", graduationDate: "2025-05", gpa: "3.7/4.0", honors: "Dean's List, Marketing Society President",
        },
      ],
      skills: [
        { id: "sk1", category: "Digital Marketing", skills: ["SEO/SEM", "Google Analytics", "Social Media Management", "Email Marketing", "A/B Testing"] },
        { id: "sk2", category: "Tools", skills: ["HubSpot", "Canva", "Adobe Creative Suite", "Hootsuite", "Mailchimp"] },
      ],
      projects: [],
      certifications: [
        { id: "c1", name: "Google Analytics Certified", issuer: "Google", issueDate: "2025-03" },
        { id: "c2", name: "HubSpot Content Marketing", issuer: "HubSpot Academy", issueDate: "2025-01" },
      ],
      languages: [{ language: "English", proficiency: "Native" }, { language: "Mandarin", proficiency: "Fluent" }],
      achievements: ["1st Place — Berkeley Marketing Case Competition 2024", "Published in Berkeley Business Review"],
      volunteerExperience: [], interests: [], references: [], customSections: [],
      sectionOrder: ["personalInfo", "summary", "workExperience", "education", "skills", "certifications", "languages", "achievements"],
    },
  },
  {
    name: "Alex Rivera",
    role: "Software Engineer",
    template: "modern-professional",
    data: {
      personalInfo: {
        fullName: "Alex Rivera",
        professionalTitle: "Senior Full-Stack Engineer",
        email: "alex.rivera@email.com",
        phone: "+1 (206) 555-0198",
        city: "Seattle",
        country: "United States",
        linkedin: "linkedin.com/in/alexrivera",
        github: "github.com/arivera",
        portfolio: "alexrivera.dev",
      },
      summary: "Full-stack engineer with 5+ years building high-traffic web applications. Led migration of monolithic architecture to microservices at CloudScale, reducing deployment time by 70% and improving uptime to 99.97%. Passionate about developer experience, performance optimization, and mentoring junior engineers.",
      workExperience: [
        {
          id: "w1", jobTitle: "Senior Software Engineer", company: "CloudScale Technologies", location: "Seattle, WA",
          startDate: "2023-03", endDate: "", currentlyWorking: true,
          responsibilities: [
            "Architected and led migration from monolith to microservices serving 2M+ daily active users",
            "Reduced API response times by 40% through Redis caching and query optimization",
            "Mentored 4 junior engineers through code reviews, pair programming, and technical design sessions",
            "Implemented CI/CD pipeline with GitHub Actions, cutting deployment time from 45min to 12min",
          ],
          achievements: [],
        },
        {
          id: "w2", jobTitle: "Software Engineer", company: "DataFlow Inc.", location: "Portland, OR",
          startDate: "2021-01", endDate: "2023-02", currentlyWorking: false,
          responsibilities: [
            "Built real-time data visualization dashboard used by 500+ enterprise customers",
            "Developed RESTful APIs handling 10K+ requests/second using Node.js and PostgreSQL",
            "Contributed to open-source charting library with 3K+ GitHub stars",
          ],
          achievements: [],
        },
        {
          id: "w3", jobTitle: "Junior Developer", company: "WebCraft Studio", location: "Portland, OR",
          startDate: "2019-06", endDate: "2020-12", currentlyWorking: false,
          responsibilities: [
            "Developed responsive web applications for 15+ clients using React and TypeScript",
            "Integrated third-party APIs including Stripe, Twilio, and SendGrid",
          ],
          achievements: [],
        },
      ],
      education: [
        {
          id: "e1", degree: "B.S. Computer Science", institution: "Oregon State University",
          location: "Corvallis, OR", graduationDate: "2019-06", gpa: "3.6/4.0",
        },
      ],
      skills: [
        { id: "sk1", category: "Languages", skills: ["TypeScript", "JavaScript", "Python", "Go", "SQL"] },
        { id: "sk2", category: "Frontend", skills: ["React", "Next.js", "Tailwind CSS", "Redux", "GraphQL"] },
        { id: "sk3", category: "Backend", skills: ["Node.js", "Express", "PostgreSQL", "Redis", "Docker", "Kubernetes"] },
        { id: "sk4", category: "Tools", skills: ["Git", "GitHub Actions", "AWS", "Datadog", "Terraform"] },
      ],
      projects: [
        {
          id: "p1", title: "DevMetrics", role: "Creator & Maintainer", duration: "2022-Present",
          technologies: ["React", "D3.js", "Node.js", "PostgreSQL"],
          description: "Open-source developer productivity dashboard with 1.2K GitHub stars. Tracks commit patterns, PR review times, and deployment frequency.",
          outcomes: [], url: "https://github.com/arivera/devmetrics",
        },
      ],
      certifications: [
        { id: "c1", name: "AWS Solutions Architect Associate", issuer: "Amazon Web Services", issueDate: "2023-06" },
      ],
      languages: [{ language: "English", proficiency: "Native" }, { language: "Spanish", proficiency: "Professional" }],
      achievements: ["Speaker at ReactConf 2024 — \"Scaling React Apps to 2M Users\"", "3x Hackathon Winner (HackTheNorth, TreeHacks, HackMIT)"],
      volunteerExperience: [], interests: [], references: [], customSections: [],
      sectionOrder: ["personalInfo", "summary", "workExperience", "skills", "projects", "education", "certifications", "languages", "achievements"],
    },
  },
  {
    name: "Priya Sharma",
    role: "Electrical Engineer",
    template: "technical-clean",
    data: {
      personalInfo: {
        fullName: "Priya Sharma",
        professionalTitle: "Senior Electrical Engineer — Power Systems",
        email: "priya.sharma@email.com",
        phone: "+1 (713) 555-0267",
        city: "Houston",
        country: "United States",
        linkedin: "linkedin.com/in/priyasharma-ee",
      },
      summary: "Electrical engineer with 8 years of experience in power systems design, renewable energy integration, and grid modernization. Led $12M substation upgrade project delivered on-time and 5% under budget. Strong background in SCADA systems, protection relay coordination, and IEEE/NEC standards compliance.",
      workExperience: [
        {
          id: "w1", jobTitle: "Senior Electrical Engineer", company: "GridTech Solutions", location: "Houston, TX",
          startDate: "2021-04", endDate: "", currentlyWorking: true,
          responsibilities: [
            "Lead engineer on $12M 138kV substation modernization project, managing team of 6 engineers",
            "Designed protection and control systems for 15+ substations using SEL relays and ETAP software",
            "Developed automated load flow analysis scripts in Python, reducing study time by 60%",
            "Coordinated with utility clients on interconnection studies for 200MW+ solar and wind projects",
          ],
          achievements: [],
        },
        {
          id: "w2", jobTitle: "Electrical Engineer", company: "PowerCore Engineering", location: "Dallas, TX",
          startDate: "2018-01", endDate: "2021-03", currentlyWorking: false,
          responsibilities: [
            "Performed short circuit, arc flash, and coordination studies for industrial facilities",
            "Designed medium-voltage switchgear and motor control center layouts for oil & gas clients",
            "Created single-line diagrams, three-line diagrams, and panel schedules using AutoCAD Electrical",
          ],
          achievements: [],
        },
      ],
      education: [
        {
          id: "e1", degree: "M.S. Electrical Engineering", institution: "University of Texas at Austin",
          location: "Austin, TX", graduationDate: "2017-12",
        },
        {
          id: "e2", degree: "B.S. Electrical Engineering", institution: "University of Houston",
          location: "Houston, TX", graduationDate: "2016-05", gpa: "3.8/4.0", honors: "Summa Cum Laude",
        },
      ],
      skills: [
        { id: "sk1", category: "Software", skills: ["ETAP", "SKM PowerTools", "AutoCAD Electrical", "MATLAB", "Python", "PSCAD"] },
        { id: "sk2", category: "Specializations", skills: ["Power Systems Analysis", "Protection & Controls", "Renewable Integration", "SCADA/EMS", "Arc Flash Studies"] },
        { id: "sk3", category: "Standards", skills: ["IEEE", "NEC", "NESC", "NERC", "IEC"] },
      ],
      projects: [
        {
          id: "p1", title: "200MW Solar Farm Interconnection", role: "Lead Engineer", duration: "2023",
          technologies: ["ETAP", "PSCAD", "Python"],
          description: "Led interconnection feasibility study and system impact analysis for utility-scale solar project. Designed reactive power compensation scheme meeting NERC reliability standards.",
          outcomes: [],
        },
      ],
      certifications: [
        { id: "c1", name: "Professional Engineer (PE)", issuer: "Texas Board of Professional Engineers", issueDate: "2020-08" },
        { id: "c2", name: "Project Management Professional (PMP)", issuer: "PMI", issueDate: "2022-11" },
      ],
      languages: [{ language: "English", proficiency: "Native" }, { language: "Hindi", proficiency: "Fluent" }],
      achievements: ["IEEE PES Outstanding Young Engineer Award 2023", "Published 3 papers in IEEE Transactions on Power Systems"],
      volunteerExperience: [], interests: [], references: [], customSections: [],
      sectionOrder: ["personalInfo", "summary", "skills", "workExperience", "projects", "education", "certifications", "languages", "achievements"],
    },
  },
  {
    name: "Marcus Johnson",
    role: "Project Manager",
    template: "minimal-executive",
    data: {
      personalInfo: {
        fullName: "Marcus Johnson",
        professionalTitle: "Senior Project Manager — Enterprise Transformations",
        email: "marcus.johnson@email.com",
        phone: "+1 (312) 555-0334",
        city: "Chicago",
        country: "United States",
        linkedin: "linkedin.com/in/marcusjohnson-pm",
      },
      summary: "Results-driven project manager with 10+ years leading cross-functional teams through complex enterprise transformations. Managed portfolio of $50M+ in concurrent projects across financial services and healthcare. Adept at stakeholder alignment, risk mitigation, and Agile/hybrid delivery methodologies. Consistently delivers on-time and under budget.",
      workExperience: [
        {
          id: "w1", jobTitle: "Senior Project Manager", company: "Apex Consulting Group", location: "Chicago, IL",
          startDate: "2020-06", endDate: "", currentlyWorking: true,
          responsibilities: [
            "Manage portfolio of 5-7 concurrent projects valued at $8M-$15M each for Fortune 500 clients",
            "Led enterprise CRM migration for 12,000-user organization, completing 3 weeks ahead of schedule",
            "Established PMO standards adopted across 40+ consultants, improving delivery predictability by 30%",
            "Conduct executive steering committee presentations and monthly stakeholder reviews",
          ],
          achievements: [],
        },
        {
          id: "w2", jobTitle: "Project Manager", company: "HealthTech Partners", location: "Chicago, IL",
          startDate: "2017-01", endDate: "2020-05", currentlyWorking: false,
          responsibilities: [
            "Delivered EHR system integration across 8 hospital sites serving 2M+ patients annually",
            "Managed $22M budget with cross-functional team of 35 developers, clinicians, and analysts",
            "Reduced go-live defects by 45% through structured UAT process and defect triage workflow",
          ],
          achievements: [],
        },
        {
          id: "w3", jobTitle: "Associate Project Manager", company: "FinServ Solutions", location: "New York, NY",
          startDate: "2014-08", endDate: "2016-12", currentlyWorking: false,
          responsibilities: [
            "Coordinated regulatory compliance projects for tier-1 banking clients (SOX, Basel III)",
            "Created project dashboards in Jira and Confluence, improving visibility for 50+ stakeholders",
          ],
          achievements: [],
        },
      ],
      education: [
        {
          id: "e1", degree: "MBA — Strategy & Operations", institution: "Northwestern University, Kellogg School",
          location: "Evanston, IL", graduationDate: "2014-06",
        },
        {
          id: "e2", degree: "B.S. Industrial Engineering", institution: "Purdue University",
          location: "West Lafayette, IN", graduationDate: "2012-05", gpa: "3.5/4.0",
        },
      ],
      skills: [
        { id: "sk1", category: "Methodologies", skills: ["Agile/Scrum", "SAFe", "Waterfall", "Hybrid", "Lean Six Sigma"] },
        { id: "sk2", category: "Tools", skills: ["Jira", "Confluence", "MS Project", "Smartsheet", "Power BI", "Tableau"] },
        { id: "sk3", category: "Domains", skills: ["Financial Services", "Healthcare IT", "Enterprise SaaS", "Regulatory Compliance"] },
      ],
      projects: [],
      certifications: [
        { id: "c1", name: "Project Management Professional (PMP)", issuer: "PMI", issueDate: "2016-03" },
        { id: "c2", name: "Certified SAFe 5 Agilist", issuer: "Scaled Agile", issueDate: "2021-09" },
        { id: "c3", name: "Lean Six Sigma Green Belt", issuer: "ASQ", issueDate: "2018-05" },
      ],
      languages: [{ language: "English", proficiency: "Native" }, { language: "French", proficiency: "Conversational" }],
      achievements: [
        "Apex Consulting \"Delivery Excellence Award\" — 2022, 2023",
        "Managed 95% on-time delivery rate across 20+ projects over 4 years",
      ],
      volunteerExperience: [], interests: [], references: [], customSections: [],
      sectionOrder: ["personalInfo", "summary", "workExperience", "education", "skills", "certifications", "languages", "achievements"],
    },
  },
  {
    name: "Olivia Nakamura",
    role: "UX Designer",
    template: "elegant-two-column",
    data: {
      personalInfo: {
        fullName: "Olivia Nakamura",
        professionalTitle: "Senior UX/UI Designer",
        email: "olivia.nakamura@email.com",
        phone: "+1 (503) 555-0189",
        city: "Portland",
        country: "United States",
        linkedin: "linkedin.com/in/olivianakamura",
        portfolio: "olivianakamura.design",
      },
      summary: "User experience designer with 6+ years crafting intuitive digital products for SaaS, fintech, and e-commerce. Led redesign of onboarding flow at PayStream, boosting activation by 34%. Combines strong research skills with pixel-perfect visual design to deliver measurable business outcomes.",
      workExperience: [
        {
          id: "w1", jobTitle: "Senior UX Designer", company: "PayStream", location: "Portland, OR",
          startDate: "2022-01", endDate: "", currentlyWorking: true,
          responsibilities: [
            "Lead end-to-end design for merchant dashboard serving 80K+ active users",
            "Redesigned onboarding flow, increasing new-user activation rate from 42% to 76%",
            "Established and maintained a design system with 120+ components in Figma",
            "Conduct weekly usability testing sessions and synthesize findings into actionable insights",
          ],
          achievements: [],
        },
        {
          id: "w2", jobTitle: "UX Designer", company: "ShopLift Commerce", location: "San Francisco, CA",
          startDate: "2019-08", endDate: "2021-12", currentlyWorking: false,
          responsibilities: [
            "Designed responsive e-commerce experiences driving $2.4M in incremental annual revenue",
            "Created user journey maps, wireframes, and high-fidelity prototypes for 10+ product features",
            "Partnered with data science team to integrate A/B testing into the design process",
          ],
          achievements: [],
        },
      ],
      education: [
        {
          id: "e1", degree: "M.F.A. Interaction Design", institution: "School of Visual Arts",
          location: "New York, NY", graduationDate: "2019-05",
        },
        {
          id: "e2", degree: "B.A. Graphic Design", institution: "University of Oregon",
          location: "Eugene, OR", graduationDate: "2017-06", gpa: "3.9/4.0", honors: "Magna Cum Laude",
        },
      ],
      skills: [
        { id: "sk1", category: "Design", skills: ["Figma", "Sketch", "Adobe XD", "Framer", "Principle"] },
        { id: "sk2", category: "Research", skills: ["Usability Testing", "User Interviews", "Card Sorting", "Heuristic Evaluation", "Survey Design"] },
        { id: "sk3", category: "Development", skills: ["HTML/CSS", "Tailwind CSS", "React Basics", "Accessibility (WCAG 2.1)"] },
      ],
      projects: [
        {
          id: "p1", title: "DesignOps Toolkit", role: "Creator", duration: "2023-Present",
          technologies: ["Figma", "Notion", "React"],
          description: "Open-source collection of design-to-dev handoff templates and component documentation standards used by 500+ designers.",
          outcomes: [], url: "https://designops-toolkit.dev",
        },
      ],
      certifications: [
        { id: "c1", name: "Google UX Design Certificate", issuer: "Google", issueDate: "2022-04" },
        { id: "c2", name: "Certified Accessibility Specialist", issuer: "IAAP", issueDate: "2023-01" },
      ],
      languages: [{ language: "English", proficiency: "Native" }, { language: "Japanese", proficiency: "Conversational" }],
      achievements: ["Featured in UX Collective's \"Top 20 Portfolios of 2024\"", "Speaker at Config 2023 — \"Building Inclusive Design Systems\""],
      volunteerExperience: [], interests: [], references: [], customSections: [],
      sectionOrder: ["personalInfo", "summary", "workExperience", "skills", "projects", "education", "certifications", "languages", "achievements"],
    },
  },
];
